import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { AppState } from '../../../../reducers';
import { StartFlowGlobalStore } from '../../../../start-flow/store/start-flow-global.store';
import { DictionaryGlobalStore } from '../../../../dictionary/store/dictionary-global.store';
import { GetLocaleStore } from '../../../../dictionary/store/get-locale.store';
import { AccountGlobalStore } from '../../../../account/store/account-global.store';
import { CreateAccountStore } from '../../../../account/store/create-account.store';
import { Language } from '../../../../dictionary/models/language.model';
import { Locale } from '../../../../dictionary/models/locale.model';
import { RoutesHelper } from '../../../../core/helpers/routes.helper';
import { CoreRoutes } from '../../../../core/routes/core.routes';
import {
  AccountRoutesEnum,
  getAccountRoute,
} from '../../../../account/routes/account-routes.enum';
import {
  authInvitationHashSelector,
  authRolesSelector,
  authUserSelector,
} from '../../../../auth/store/auth.selectors';
import { tenantRolesAsStringsSelector } from '../../../../core/store/core.selectors';
import { PrimaryResidence } from '../../../../account/models/primary-residence.model';
import { Country } from '../../../../shared/sd-forms/models/country.model';

@Injectable()
export class TdStartFlowStore {
  private readonly startFlowStore = inject(StartFlowGlobalStore);
  private readonly ngrxStore = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly dictionaryGlobalStore = inject(DictionaryGlobalStore);
  private readonly getLocaleStore = inject(GetLocaleStore);
  private readonly accountGlobalStore = inject(AccountGlobalStore);
  private readonly createAccountStore = inject(CreateAccountStore);

  initialize(): void {
    this.startFlowStore.setDictionaryLocaleOverride(() =>
      this.loadBrowserDictionary()
    );
    this.startFlowStore.setNewAccountNavigationOverride(() =>
      this.navigateNewAccount()
    );
  }

  /**
   * Resolves the best locale (auth → hash → browser) and loads the dictionary.
   * Throws on failure so the caller can handle navigation.
   */
  private async loadBrowserDictionary(): Promise<void> {
    const languages = this.dictionaryGlobalStore.languagesFiltered();
    const authUser = this.ngrxStore.selectSignal(authUserSelector)();
    const hash = this.ngrxStore.selectSignal(authInvitationHashSelector)();

    const authLocale = authUser?.locale
      ? Language.getLocalesByLanguageCode(languages, authUser.locale)
      : null;
    const hashLocale =
      hash?.locale?.length > 0
        ? Language.getLocaleByLocaleId(languages, hash.locale)
        : null;
    const browserLocale = Language.getBrowserLocale(languages);
    const locale = authLocale ?? hashLocale ?? browserLocale;

    if (locale) {
      this.dictionaryGlobalStore.setLocaleDefault(locale);
      this.getLocaleStore.setLocaleDefault(locale);
    }

    const localeId = Locale.getLocaleId(locale);
    await this.getLocaleStore.loadDictionaryForRegisterAndLogin(localeId);
  }

  /**
   * TD-specific new-account navigation: federation checks determine whether
   * the user sees the forbidden page, gets auto-created, or sees the create form.
   * Returns true if navigation was handled, false to let the default path run.
   */
  private navigateNewAccount(): boolean {
    const authUser = this.ngrxStore.selectSignal(authUserSelector)();
    const roles = this.ngrxStore.selectSignal(authRolesSelector)();
    const tenantRoles = this.ngrxStore.selectSignal(
      tenantRolesAsStringsSelector
    )();
    const queryParams = RoutesHelper.getParams(false);

    const isFederated = authUser?.federationId?.length > 0;
    if (!isFederated) return false;

    const hasMatchingRole = roles.some(role =>
      tenantRoles.some(tenantRole => tenantRole === role)
    );

    if (!hasMatchingRole) {
      this.router.navigate([`${CoreRoutes.root}/${CoreRoutes.forbidden}`], {
        queryParams,
      });
      return true;
    }

    const languages = this.dictionaryGlobalStore.languagesFiltered();
    const hash = this.ngrxStore.selectSignal(authInvitationHashSelector)();
    const authLocale = authUser?.locale
      ? Language.getLocalesByLanguageCode(languages, authUser.locale)
      : null;
    const hashLocale =
      hash?.locale?.length > 0
        ? Language.getLocaleByLocaleId(languages, hash.locale)
        : null;
    const locale =
      authLocale ?? hashLocale ?? Language.getBrowserLocale(languages);

    const primaryResidenceSelected = new PrimaryResidence();
    primaryResidenceSelected.country =
      Country.getCountryByCountryCode(locale.countryCode)?.countryName ??
      locale.countryCode;

    this.createAccountStore.createAccount({
      form: {
        locale,
        firstName: authUser.givenName,
        lastName: authUser.familyName,
        primaryResidenceSelected,
      },
      callback: ({ account, error }) => {
        if (account) {
          this.accountGlobalStore.setAccount(account);
          this.startFlowStore.invitationUsed();
          this.startFlowStore.accountCreated(account);
        }
        if (error) {
          this.router.navigate(
            [`/${CoreRoutes.root}/${CoreRoutes.noAccount}`],
            { queryParams }
          );
        }
      },
    });
    return true;
  }
}
